import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface Props {
  value: string;        // YYYY-MM-DD or ''
  onChange: (d: string) => void;
  id?: string;          // applied to the Month select for label association
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  dark?: boolean;
  compact?: boolean;
  max?: string;         // YYYY-MM-DD — defaults to today
}

function daysInMonth(year: string, month: string): number {
  if (!year || !month) return 31;
  return new Date(parseInt(year), parseInt(month), 0).getDate();
}

export function BirthdatePicker({ value, onChange, id, 'aria-describedby': ariaDescribedby, 'aria-invalid': ariaInvalid, dark = false, compact = false, max }: Props) {
  // Internal state tracks partial selections so individual dropdowns persist
  const [year, setYear]   = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay]     = useState('');

  // Sync internal state when the parent value changes (e.g. "Use my details")
  useEffect(() => {
    if (value) {
      const parts = value.split('-');
      setYear(parts[0] ?? '');
      setMonth(parts[1] ?? '');
      setDay(parts[2] ?? '');
    } else {
      setYear('');
      setMonth('');
      setDay('');
    }
  }, [value]);

  const maxYear = max ? parseInt(max.split('-')[0]) : new Date().getFullYear();
  const minYear = 1920;
  const years   = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  const days    = Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1);

  function commit(y: string, m: string, d: string) {
    setYear(y);
    setMonth(m);
    setDay(d);
    // Only emit a full date when all three parts are set
    if (y && m && d) {
      const maxDay    = daysInMonth(y, m);
      const clampedD  = String(Math.min(parseInt(d), maxDay)).padStart(2, '0');
      onChange(`${y}-${m.padStart(2, '0')}-${clampedD}`);
    }
  }

  const selectCls = clsx(
    'appearance-none cursor-pointer transition-colors',
    dark
      ? clsx(
          'rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500',
          compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-3 text-sm',
          'border-white/10 bg-white/5 text-white hover:border-brand-400 [color-scheme:dark]',
        )
      : compact
        ? 'rounded-lg border border-card bg-card text-body hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500 px-2 py-1.5 text-xs'
        : 'field-input !px-3',
  );

  return (
    <div className="flex gap-2" role="group">
      {/* Month — labelled by the Field's <label htmlFor={id}> */}
      <select
        id={id}
        value={month}
        onChange={e => commit(year, e.target.value, day)}
        className={clsx(selectCls, 'flex-[2]')}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
      >
        <option value="">Month</option>
        {MONTHS.map((m, i) => (
          <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
        ))}
      </select>

      {/* Day */}
      <select
        value={day}
        onChange={e => commit(year, month, e.target.value)}
        className={clsx(selectCls, 'flex-[1]')}
      >
        <option value="">Day</option>
        {days.map(d => (
          <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
        ))}
      </select>

      {/* Year */}
      <select
        value={year}
        onChange={e => commit(e.target.value, month, day)}
        className={clsx(selectCls, 'flex-[1.5]')}
      >
        <option value="">Year</option>
        {years.map(y => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
    </div>
  );
}
